import Core from 'handsontable/core';
import { BasePlugin } from 'handsontable/plugins';
import CellCoords from 'handsontable/3rdparty/walkontable/src/cell/coords';
import { SelectionController } from 'handsontable/selection';
import { isEmpty } from 'lodash';

export const MILANOTE_CONTEXT_MENU_PLUGIN_NAME = 'milanoteContextMenu';

/**
 * MilanoteContextMenuPlugin.ts
 *  - The purpose of this plugin is to update the way context menu is shown in Milanote table elements, we only
 *    want to show handsontable context menu when one or more cells are selected on the table. Otherwise, show Milanote
 *    context menu
 *  - The way we are doing this is by removing the default handsontable context menu event listeners at the start,
 *    and only reapply it when there is a cell selection on the table
 *
 */
class MilanoteContextMenuPlugin extends BasePlugin {
    private contextMenuEventListeners: any[] | null;
    private mouseDownEventListener: any | null;

    /**
     * This key will be used as a prop key when enabling this plugin in the HotTable component
     */
    static get PLUGIN_KEY(): string {
        return MILANOTE_CONTEXT_MENU_PLUGIN_NAME;
    }

    constructor(hotInstance: Core) {
        super(hotInstance);

        this.contextMenuEventListeners = null;
        this.mouseDownEventListener = null;
    }

    isEnabled(): boolean {
        return true;
    }

    /**
     * The `enablePlugin` method is triggered on the `beforeInit` hook.
     * It should contain the plugin's initial setup and hook connections.
     * This method is run only if the `isEnabled` method returns `true`.
     */
    enablePlugin(): void {
        this.addHook('beforeOnCellMouseDown', (...args) => this.onBeforeOnCellMouseDown(...args));
        this.addHook('afterContextMenuShow', () => this.onAfterContextMenuShow());
        this.addHook('afterContextMenuHide', () => this.onAfterContextMenuHide());
        this.addHook('afterSelectionEnd', (...args) => this.onAfterSelectionEnd(...args));
        this.addHook('afterDeselect', (...args) => this.onAfterDeselect(...args));

        // The `super` method sets the `this.enabled` property to `true`.
        // It is a necessary step to update the plugin's settings properly.
        super.enablePlugin();
    }

    onBeforeOnCellMouseDown(
        event: MouseEvent,
        coords: CellCoords,
        TD: HTMLTableCellElement,
        controller: SelectionController,
    ): void {
        // If we right click on a table when there is no cell selection, we want to prevent the cell that is
        // right-clicked on to be selected. This is because we want to show the Milanote context menu in this case
        const isRightMouseClick = event.button === 2;
        if (isRightMouseClick && isEmpty(this.hot.getSelected())) {
            // @ts-ignore - Handsontable private property. This will prevent stop propagation on Handsontable only,
            // but still allow our element-level event listeners to be triggered
            event.isImmediatePropagationEnabled = false;
        }
    }

    onAfterSelectionEnd(row: number, column: number, row2: number, column2: number, selectionLayerLevel: number): void {
        this.addHotContextMenuEventListeners();
    }

    onAfterDeselect(): void {
        this.removeHotContextMenuEventListeners();
    }

    addHotContextMenuEventListeners(): void {
        if (this.contextMenuEventListeners === null) return;

        this.contextMenuEventListeners.forEach(({ element, event, callback, eventManager }) =>
            eventManager.addEventListener(element, event, callback),
        );

        this.contextMenuEventListeners = null;
    }

    removeHotContextMenuEventListeners(): void {
        if (this.contextMenuEventListeners !== null) return;

        // Collect all potential 'contextmenu' event listeners from handsontable
        this.contextMenuEventListeners = [
            // @ts-ignore - Handsontable private property
            ...(this.hot.eventListeners || []),

            // @ts-ignore - Handsontable private property
            ...(this.hot.getPlugin('contextMenu')?.menu?.eventListeners || []),

            // @ts-ignore - Handsontable private property
            ...(this.hot.view?._wt?.eventListeners || []),
        ].filter((listener) => listener.event === 'contextmenu');

        this.contextMenuEventListeners.forEach(({ element, event, callback, eventManager }) =>
            eventManager.removeEventListener(element, event, callback),
        );
    }

    /**
     * There is an issue on iPad where after Handsontable context menu is shown, any selection on the table will not
     * hide the context menu. This is due to the fact that hiding context menu is only triggered by a 'mousedown' event.
     *
     * This function applies the same event listener for 'touchstart' event to hide the context menu for iPad as well.
     */
    onAfterContextMenuShow(): void {
        if (this.mouseDownEventListener !== null) return;

        // @ts-ignore - Handsontable private property
        const contextMenuPluginEventListeners = this.hot.getPlugin('contextMenu')?.menu?.eventListeners;

        // @ts-ignore - Handsontable private property
        this.mouseDownEventListener = contextMenuPluginEventListeners?.find(({ event }) => event === 'mousedown');
        if (this.mouseDownEventListener) {
            const { element, callback, eventManager } = this.mouseDownEventListener;

            eventManager.addEventListener(element, 'touchstart', callback);
        }
    }

    onAfterContextMenuHide(): void {
        if (this.mouseDownEventListener === null) return;

        const { element, callback, eventManager } = this.mouseDownEventListener;

        eventManager.removeEventListener(element, 'touchstart', callback);

        this.mouseDownEventListener = null;
    }
}

export default MilanoteContextMenuPlugin;
